<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-layout row wrap justify-space-between>
        <dt-search-box
          :search="search"
          @typing="searchValue"
          @reset-datatable="resetDataTable()"
          @clear-search-field="search = null"
        />
        <v-layout row wrap justify-end class="pt-2 pb-2">
          <v-btn dark color="primary" v-if="hasPermissions(['production_store'])" @click="dialogs.addDialog = true">
            <v-icon>add</v-icon>
            {{ $t('datatable-add-new') }}
          </v-btn>
        </v-layout>
      </v-layout>
    </v-card>

    <v-card flat>
      <v-layout row wrap justify-end>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 0px; height: 30px; width: 30px"
              :loading="loadThePrintBtn"
              flat
              fab
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('print')"
            >
              <v-icon>print</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Print Table`) }}</span>
        </v-tooltip>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 20px 5px 10px; height: 30px; width: 30px"
              fab
              :loading="loadTheExportBtn"
              flat
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('export')"
            >
              <v-icon>fa-file-excel</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Export Table`) }}</span>
        </v-tooltip>
      </v-layout>
    </v-card>

    <v-card class="px-4 pb-4">
      <v-data-table
        v-show="isDatatable"
        :headers="headers"
        :items="resultData.data"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="pagination.rowsPerPageItems"
        :total-items="resultData.total"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.production_id }}</td>-->
          <td>{{ props.item.bill_auto_number }}</td>
          <td>{{ props.item.factory_name }}</td>
          <td>{{ props.item.product_name }}</td>
          <td>{{ props.item.production_quantity | currency }}</td>
          <td>{{ props.item.mixture_name }}</td>
          <td>{{ props.item.used_mixture_quantity | currency }}</td>
          <td>{{ props.item.mixture_cost | currency }}</td>
          <td>{{ $t(props.item.shift_time) }}</td>
          <td>{{ props.item.production_date }}</td>
          <td>
            <v-tooltip top>
              <v-chip
                slot="activator"
                label
                :color="props.item.production_status === 'Open' ? 'error' : 'success'"
                text-color="white"
                class="no-select"
              >
                <v-icon>{{ props.item.production_status === 'Open' ? 'fa-lock-open' : 'fa-lock' }}</v-icon>
              </v-chip>
              <span>{{ props.item.production_status === 'Open' ? $t('Open') : $t('Close') }}</span>
            </v-tooltip>
          </td>
          <td v-if="hasActionsMenuPermissions()">
            <v-menu offset-y bottom :left="!$vuetify.rtl" :right="$vuetify.rtl" transition="slide-y-reverse-transition">
              <v-btn flat fab icon class="transparent py-2" slot="activator">
                <v-icon large class="grey--text actions-btn">more_vert</v-icon>
              </v-btn>
              <v-list>
                <v-tooltip v-if="hasPermissions(['production_show'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="viewRecord(props.item.production_id)">
                    <v-icon>chrome_reader_mode</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-view') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`View Production information`) }}</span>
                </v-tooltip>

                <v-tooltip
                  v-if="hasPermissions(['production_update']) && props.item.production_status === 'Open'"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="editRecord(props.item.production_id)">
                    <v-icon>edit</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-edit') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`Edit Production information`) }}</span>
                </v-tooltip>

                <v-tooltip
                  v-if="hasPermissions(['production_close']) && props.item.production_status === 'Open'"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="closeProductionRecord(props.item.production_id)">
                    <v-icon>fa-lock</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-close') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t('Close Production') }}</span>
                </v-tooltip>

                <v-tooltip
                  v-if="hasPermissions(['production_unclose']) && props.item.production_status === 'Close'"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="openProductionRecord(props.item.production_id)">
                    <v-icon>fa-lock-open</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-open') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`Open Production`) }}</span>
                </v-tooltip>

                <v-divider v-if="hasPermissions(['production_destroy']) && props.item.production_status === 'Open'" />

                <v-tooltip
                  v-if="hasPermissions(['production_destroy']) && props.item.production_status === 'Open'"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="showConfirmDeletion(props.item.production_id)">
                    <v-icon class="red--text">delete</v-icon>
                    <v-list-tile-content class="red--text px-3">{{ $t('datatable-delete') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t('Delete Production Record') }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
          $t('Sorry, No results found for your search criteria')
        }}</v-alert>
      </v-data-table>

      <dt-pagination
        :resultData="resultData"
        :pagination="pagination"
        :perPageNumber="perPageNumber"
        @page-changed="pageChanged"
        @per-page-changed="resultsPerPageChanged"
      />
    </v-card>

    <view-production :viewDialog="dialogs.viewDialog" @hidemodal="dialogs.viewDialog = false" />

    <add-production
      :addDialog="dialogs.addDialog"
      @reset-datatable="resetDataTable(), resetAllErrors(true)"
      @hidemodal="resetAllErrors(true), (dialogs.addDialog = false)"
      @add-staff="dialogs.addStaff = true"
      @add-factory="dialogs.addFactory = true"
      @add-product="dialogs.addProduct = true"
      @add-mixture="dialogs.addMixture = true"
    />

    <edit-production
      :editDialog="dialogs.editDialog"
      @updated="updateDatatableResult(), (dialogs.editDialog = false), (allowGettingData = false)"
      @hidemodal="resetAllErrors(true), (dialogs.editDialog = false)"
      @add-staff="dialogs.addStaff = true"
      @add-factory="dialogs.addFactory = true"
      @add-product="dialogs.addProduct = true"
      @add-mixture="dialogs.addMixture = true"
    />

    <confirm-deletion
      :deleteDialog="dialogs.deleteDialog"
      :btnDeleteLoading="btnDeleteLoading"
      @hidemodal="dialogs.deleteDialog = false"
      @confirm="deleteAction()"
    />

    <print-export-component
      :printExportDialog="dialogs.printExportDialog"
      :tableHeader="tableHeader"
      :tableBody="tableBody"
      :clickedButton="clickedButton"
      @hidemodal="dialogs.printExportDialog = false"
    />

    <add-staff
      :addDialog="dialogs.addStaff"
      @reset-datatable="setFullStaffList()"
      @hidemodal="resetAllErrors(true), (dialogs.addStaff = false)"
    />

    <add-factory
      :addDialog="dialogs.addFactory"
      @reset-datatable="setFullFactoriesList()"
      @hidemodal="resetAllErrors(true), (dialogs.addFactory = false)"
    />

    <add-product
      :addDialog="dialogs.addProduct"
      @reset-datatable="setFullProductList()"
      @hidemodal="resetAllErrors(true), (dialogs.addProduct = false)"
      @add-product-type="dialogs.addProductType = true"
    />

    <add-mixture
      :addDialog="dialogs.addMixture"
      @reset-datatable="setFullMixtureList()"
      @hidemodal="resetAllErrors(true), (dialogs.addMixture = false)"
      @add-raw-material="dialogs.addRawMaterial = true"
    />

    <add-product-type
      :addDialog="dialogs.addProductType"
      @reset-datatable="updateFullProductTypesList()"
      @hidemodal="resetAllErrors(true), (dialogs.addProductType = false)"
    />

    <add-raw-material
      :addDialog="dialogs.addRawMaterial"
      @reset-datatable="fetchRawMaterialsList()"
      @hidemodal="resetAllErrors(true), (dialogs.addRawMaterial = false)"
      @add-product-type="dialogs.addProductType = true"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import Axios from 'axios'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    dtPagination: () => import('@/views/_partials/AppDatatablePagination.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue'),
    addProduction: () => import('./addProduction.vue'),
    editProduction: () => import('./editProduction.vue'),
    viewProduction: () => import('./viewProduction.vue'),
    addStaff: () => import('@/views/staff/staff/addStaff.vue'),
    addFactory: () => import('@/views/inventory/factories/addFactory.vue'),
    addProduct: () => import('@/views/inventory/products/addProduct.vue'),
    addMixture: () => import('@/views/inventory/mixtures/addMixture.vue'),
    addProductType: () => import('@/views/inventory/productTypes/addProductType.vue'),
    addRawMaterial: () => import('@/views/inventory/rawMaterials/addRawMaterial.vue')
  },

  data() {
    return {
      dialogs: {
        viewDialog: false,
        addDialog: false,
        editDialog: false,
        deleteDialog: false,
        printExportDialog: false,
        addStaff: false,
        addProduct: false,
        addFactory: false,
        addMixture: false,
        addProductType: false,
        addRawMaterial: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      deleteId: 0,
      btnDeleteLoading: false,
      isDatatable: true,
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'production_id',
      perPageNumber: 10,
      pagination: {
        sortBy: this.defaultSortBy,
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 1
      },
      oldPagination: {},
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'production_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Bill Number'), value: 'bill_auto_number' },
        { text: this.$t('Factory'), value: 'factory_name' },
        { text: this.$t('Product Name'), value: 'product_name' },
        { text: this.$t('Production Quantity'), value: 'production_quantity' },
        { text: this.$t('Mixture Name'), value: 'mixture_name' },
        { text: this.$t('Mixture Quantity'), value: 'used_mixture_quantity' },
        {
          text: `${this.$t('Mixture Cost')} (${localStorage.systemCurrency})`,
          value: 'mixture_cost'
        },
        { text: this.$t('Shift Time'), value: 'shift_time' },
        { text: this.$t('Production Date'), value: 'production_date' },
        { text: this.$t('Status'), value: 'production_status' }
      ],
      allowGettingData: true
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: this.$t('Productions'),
      icon: 'fa-dolly'
    })

    if (this.hasActionsMenuPermissions()) {
      this.headers.push({
        text: this.$t('datatable-actions'),
        value: '',
        sortable: false
      })
    }

    this.setFullStaffList()
    this.setFullFactoriesList()
    this.setFullProductList()
    this.setFullMixtureList()
    this.fetchRawMaterialsList()
    this.updateFullProductTypesList()
    this.fetchRawMaterialsList()
  },

  computed: {
    ...mapGetters({
      resultData: 'productions/getProductionsList'
    })
  },

  watch: {
    pagination: {
      immediate: true,
      deep: true,
      handler() {
        let p = this.pagination
        let op = this.oldPagination
        if (p.sortBy !== op.sortBy || p.descending !== op.descending) {
          p.sortBy = p.sortBy != null ? p.sortBy : 'production_id'
          op.sortBy = p.sortBy
          op.descending = p.descending
          p.page = 1
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        } else if (this.allowGettingData || this.search !== this.oldSearch) {
          this.oldSearch = this.search
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        }
      }
    }
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'productions/updateProductionsList',
      deleteRecord: 'productions/deleteProduction',
      fetchProductionInfo: 'productions/fetchProductionInfo',
      closeProduction: 'productions/closeProduction',
      uncloseProduction: 'productions/uncloseProduction',
      setFullStaffList: 'staff/setFullStaffsList',
      setFullFactoriesList: 'factories/setFullFactoriesList',
      setFullProductList: 'products/setFullProductsList',
      setFullMixtureList: 'mixtures/fetchFullMixturesList',
      fetchRawMaterialsList: 'rawMaterials/fetchFullRawMaterialsList',
      updateFullProductTypesList: 'productTypes/updateFullProductTypesList'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    hasActionsMenuPermissions() {
      return helpers.userHasPermissions([
        'production_show',
        'production_update',
        'production_destroy',
        'production_close',
        'production_unclose'
      ])
    },

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get('production_index', {
        params: { is_paginated: 0, search: this.search }
      })
        .then(response => {
          this.tableBody = response.data.data
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    updateDatatableResult() {
      this.loading = true
      let payload = { ...this.pagination, search: this.search }
      this.updateResult(payload)
        .then(() => {
          this.pagination.page = this.resultData.current_page
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    resetDataTable() {
      this.allowGettingData = true
      this.pagination = {
        sortBy: null,
        descending: true,
        page: 1,
        rowsPerPage: this.perPageNumber,
        totalItems: 1
      }
      this.search = this.oldSearch = null
    },

    resultsPerPageChanged(val) {
      this.perPageNumber = val
      this.pagination.rowsPerPage = val
      this.pagination.page = 1
      this.updateDatatableResult()
    },

    pageChanged(val) {
      this.pagination.page = val
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
    },

    viewRecord(id) {
      this.fetchProductionInfo(id).then(() => {
        this.dialogs.viewDialog = true
      })
    },

    editRecord(id) {
      this.fetchProductionInfo(id).then(() => {
        this.dialogs.editDialog = true
      })
    },

    showConfirmDeletion(id) {
      this.deleteId = id
      this.dialogs.deleteDialog = true
    },

    deleteAction() {
      this.btnDeleteLoading = true
      this.deleteRecord(this.deleteId)
        .then(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
          let payload = {
            ...this.pagination,
            search: this.search
          }
          this.updateResult(payload)
        })
        .catch(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
        })
    },

    closeProductionRecord(id) {
      this.closeProduction(id).then(() => {
        let payload = { ...this.pagination, search: this.search }
        this.updateResult(payload)
      })
    },

    openProductionRecord(id) {
      this.uncloseProduction(id).then(() => {
        let payload = { ...this.pagination, search: this.search }
        this.updateResult(payload)
      })
    }
  }
}
</script>

<style scoped />
